import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import QRCode from '@/components/ui/qr-code'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { Feature } from '@/enum/global.enum'
import { usePermissions } from '@/hooks/use-permissions'
import { Copy, Edit, Link2, QrCode, Share2, X } from 'lucide-react'
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { toast } from 'sonner'

/**
 * Tracks how many Modals are open above the current one (0 = top-level). Each
 * Modal bumps the value for its subtree, so any modal opened from inside another
 * modal (e.g. an "Add New Department" form launched from an "Add Staff" modal)
 * can compute a z-index guaranteed to sit above its parent - for any depth.
 */
const ModalDepthContext = createContext(0)

const Modal = ({
  open,
  setOpen,
  title,
  children,
  size = 'md',
  hideCloseButton,
  closeOnOutsideClick = false,
  onHandleClose,
  nested = false,
  portalToBody = false,
  customZIndex = 'z-[1000]',
  onEdit,
  feature,
  shareLink,
  closeOnEdit = true,
  titleIcon,
  rightJSX,
}: {
  open: boolean
  setOpen?: (data: boolean) => void
  title?: string
  children?: ReactNode
  size?: 'sm' | 'md' | 'xl' | 'lg'
  hideCloseButton?: boolean
  closeOnOutsideClick?: boolean
  onHandleClose?: () => void
  nested?: boolean
  /** Portal to document.body so the modal stacks above body-level overlays (e.g. fullscreen text editor). */
  portalToBody?: boolean
  customZIndex?: string
  onEdit?: () => void
  feature?: Feature
  shareLink?: string
  closeOnEdit?: boolean
  titleIcon?: ReactNode
  rightJSX?: ReactNode
}) => {
  const { canEdit } = usePermissions()

  // How many modals are already open above this one (0 = top-level)
  const ancestorModalCount = useContext(ModalDepthContext)
  const isNestedInModal = ancestorModalCount > 0

  const modalRef = useRef<HTMLDivElement>(null)
  const [wasOpen, setWasOpen] = useState(open)
  const closeTriggeredRef = useRef(false)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        closeTriggeredRef.current = true
        if (setOpen) setOpen(false)
      }
    }

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeTriggeredRef.current = true
        if (setOpen) setOpen(false)
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscapeKey)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [open, setOpen])

  useEffect(() => {
    if (wasOpen && !open) {
      if (onHandleClose) onHandleClose()
      closeTriggeredRef.current = false
    }
    setWasOpen(open)
  }, [open, wasOpen, onHandleClose])

  if (!open) return null

  const handleModalClose = () => {
    closeTriggeredRef.current = true
    if (setOpen) setOpen(false)
  }

  // A modal opened from inside another modal must always stack above its parent.
  // Because a parent can use a high custom z-index (e.g. z-[99999]), such modals
  // use an even higher, depth-based z-index applied inline (10 per level), and
  // portal to the body to escape any parent overflow/stacking context.
  //
  // This very high z-index is reserved for modals that are launched *from* an
  // overlay/popover and therefore must sit above it (e.g. the "Add new option"
  // modals opened from a Select, which use `customZIndex`). A modal that only
  // *contains* form fields opts in with `nested`, and must instead stay below
  // the popover ceiling (Select/Popover portals render at z-[9999]) so its own
  // dropdowns remain visible. Otherwise the modal would cover its own dropdowns.
  const useAutoDepthZIndex = isNestedInModal && !nested
  const nestedZIndex = 100000 + ancestorModalCount * 10
  const zIndexClass = useAutoDepthZIndex ? '' : nested ? 'z-[1002]' : customZIndex
  const shouldPortal = isNestedInModal || nested || portalToBody

  const backdropClass = isNestedInModal || nested ? 'bg-black/40' : 'bg-black/60 backdrop-blur-sm'

  const modalElement = (
    <div
      data-modal
      className={`fixed inset-0 ${zIndexClass} ${backdropClass} flex items-center justify-center p-4 animate-in fade-in duration-300`}
      style={useAutoDepthZIndex ? { zIndex: nestedZIndex } : undefined}
    >
      <div
        ref={closeOnOutsideClick ? modalRef : null}
        className={`
          ${
            size == 'sm'
              ? 'lg:w-1/3 md:w-[60%] w-[98%]'
              : size === 'md'
                ? 'lg:w-1/2 md:w-[80%] w-[98%]'
                : size === 'xl'
                  ? 'md:w-[85%] w-[98%]'
                  : 'md:w-[90%] w-[98%]'
          }
          flex flex-col  w-full bg-white dark:bg-card rounded-lg shadow-xl border relative overflow-hidden animate-in zoom-in-95 duration-300`}
      >
        {title && (
          <div className='flex items-center justify-between p-6 border-b '>
            <div className='flex items-center gap-3'>
              {titleIcon && titleIcon}
              <h2 className='text-lg  text-gray-900 dark:text-white'>{title}</h2>
            </div>

            <div className='flex gap-2 justify-end '>
              {onEdit && canEdit(feature) && (
                <button
                  type='button'
                  className='w-8 h-8 cursor-pointer hover:bg-primary/5  rounded-full flex items-center justify-center transition-colors duration-200'
                  onClick={() => {
                    onEdit()
                    if (closeOnEdit) handleModalClose()
                  }}
                >
                  <Edit className='w-3.5 h-3.5' />
                </button>
              )}
              {shareLink && (
                <Dialog>
                  <DialogTrigger asChild>
                    <button
                      type='button'
                      className='w-8 h-8 cursor-pointer hover:bg-primary/5  rounded-full flex items-center justify-center transition-colors duration-200'
                    >
                      <Share2 className='w-3.5 h-3.5' />
                    </button>
                  </DialogTrigger>
                  <DialogContent
                    className='sm:max-w-md z-[1003] bg-white dark:bg-card'
                    overlayClassName='z-[1003] bg-black/10'
                  >
                    <DialogHeader>
                      <DialogTitle>Share</DialogTitle>
                      <DialogDescription>Anyone who has this link will be able to view this.</DialogDescription>
                    </DialogHeader>
                    <Tabs defaultValue='link' className='w-full'>
                      <TabsList className='grid w-full grid-cols-2'>
                        <TabsTrigger value='link' className='gap-2'>
                          <Link2 className='h-4 w-4' />
                          Link
                        </TabsTrigger>
                        <TabsTrigger value='qr' className='gap-2'>
                          <QrCode className='h-4 w-4' />
                          QR Code
                        </TabsTrigger>
                      </TabsList>
                      <TabsContent value='link' className='mt-4 space-y-4'>
                        <div className='flex items-center gap-2'>
                          <div className='grid flex-1 gap-2'>
                            <Label htmlFor='link' className='sr-only'>
                              Link
                            </Label>
                            <Input id='link' defaultValue={`${window.location.origin}/${shareLink}`} readOnly />
                          </div>
                        </div>
                        <DialogFooter className='sm:justify-end'>
                          <DialogClose asChild>
                            <Button
                              type='button'
                              size={'sm'}
                              onClick={async () => {
                                const textToCopy = `${window.location.origin}/${shareLink}`
                                try {
                                  if (navigator.clipboard && window.isSecureContext) {
                                    await navigator.clipboard.writeText(textToCopy)
                                    toast.success('Link copied to clipboard')
                                  } else {
                                    const textArea = document.createElement('textarea')
                                    textArea.value = textToCopy
                                    textArea.style.position = 'fixed'
                                    textArea.style.left = '-999999px'
                                    textArea.style.top = '-999999px'
                                    document.body.appendChild(textArea)
                                    textArea.focus()
                                    textArea.select()
                                    const successful = document.execCommand('copy')
                                    document.body.removeChild(textArea)
                                    if (successful) toast.success('Link copied to clipboard')
                                    else toast.error('Failed to copy link')
                                  }
                                } catch {
                                  try {
                                    const textArea = document.createElement('textarea')
                                    textArea.value = textToCopy
                                    textArea.style.position = 'fixed'
                                    textArea.style.left = '-999999px'
                                    textArea.style.top = '-999999px'
                                    document.body.appendChild(textArea)
                                    textArea.focus()
                                    textArea.select()
                                    document.execCommand('copy')
                                    document.body.removeChild(textArea)
                                    toast.success('Link copied to clipboard')
                                  } catch {
                                    toast.error('Failed to copy link. Please copy manually.')
                                  }
                                }
                              }}
                            >
                              <Copy className='h-4 w-4' />
                              Copy Link
                            </Button>
                          </DialogClose>
                        </DialogFooter>
                      </TabsContent>
                      <TabsContent value='qr' className='mt-4'>
                        <div className='flex flex-col items-center justify-center py-4'>
                          <QRCode
                            value={`${window.location.origin}/${shareLink}`}
                            size={200}
                            downloadFileName='share-qr-code'
                          />
                          <p className='text-xs text-muted-foreground mt-4 text-center'>
                            Scan this QR code with a mobile device to open the link
                          </p>
                        </div>
                      </TabsContent>
                    </Tabs>
                  </DialogContent>
                </Dialog>
              )}

              {rightJSX ? <div className='pr-2'>{rightJSX}</div> : null}
              {!hideCloseButton && (
                <button
                  type='button'
                  className='w-8 h-8 cursor-pointer hover:bg-destructive/5  rounded-full flex items-center justify-center transition-colors duration-200'
                  onClick={handleModalClose}
                >
                  <X className='w-4 h-4 text-destructive' />
                </button>
              )}
            </div>
          </div>
        )}

        <div className='py-6'>
          <div className='max-h-[80vh] px-6 overflow-y-auto pb-2'>{children}</div>
        </div>
      </div>
    </div>
  )

  // Advertise a deeper level to any modal opened from within this one
  const rendered = (
    <ModalDepthContext.Provider value={ancestorModalCount + 1}>{modalElement}</ModalDepthContext.Provider>
  )

  return shouldPortal ? createPortal(rendered, document.body) : rendered
}

export default Modal
